import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

const EFFECTIVE_DATE = "August 13, 2026";

function TermsOfService() {
  return (
    <div className="lp privacy-page">
      <header className="privacy-header">
        <Link to="/" className="privacy-logo">
          <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" />
          <span>Photentra</span>
        </Link>
        <Link to="/" className="privacy-back">Back to home</Link>
      </header>

      <main className="privacy-content">
        <h1>Terms of Service</h1>
        <p className="privacy-updated">Effective {EFFECTIVE_DATE}</p>

        <p>
          Welcome to Photentra. By creating an account or using any part of the Photentra
          platform ("Service"), you agree to these Terms of Service ("Terms"). Please read
          them carefully. If you do not agree, do not use the Service.
        </p>
        <p>
          Photentra is operated by Photentra ("we," "us," or "our"). These Terms apply to
          photographers, studio owners, and their clients who access the Service.
        </p>

        <h2>1. Eligibility</h2>
        <p>
          You must be at least 18 years old and capable of forming a legally binding
          agreement to use Photentra. By using the Service you represent that you meet
          these requirements.
        </p>

        <h2>2. Your Account</h2>
        <p>
          You are responsible for maintaining the confidentiality of your login credentials
          and for all activity that occurs under your account. Notify us immediately at{" "}
          <a href="mailto:photentra@gmail.com">photentra@gmail.com</a> if you suspect
          unauthorised access. We are not liable for losses caused by unauthorised use of
          your account.
        </p>

        <h2>3. Subscription Plans and Payments</h2>
        <p>
          Photentra offers free and paid subscription plans. Paid plans are billed in
          advance on a monthly or annual basis. All payments are processed securely through
          PayU and are subject to PayU's terms.
        </p>
        <ul>
          <li>Subscription fees are non-refundable except as required by applicable law.</li>
          <li>
            If a payment fails, your account may be downgraded to the free tier until
            payment is successfully processed.
          </li>
          <li>
            We reserve the right to change pricing with at least 14 days' notice sent to
            your registered email address.
          </li>
        </ul>

        <h2>4. Wallet</h2>
        <p>
          Photentra provides an in-app wallet that can be topped up using a payment gateway.
          Wallet credits can be used toward eligible in-app purchases (such as photo frame
          orders). Wallet balances are non-transferable, non-refundable, and carry no
          monetary value outside the platform. Credits do not expire as long as your account
          is active.
        </p>

        <h2>5. Photo Frame Orders</h2>
        <p>
          Photo frame orders are fulfilled by third-party printing and shipping partners.
          Once an order is placed and payment is confirmed, it cannot be cancelled. Disputes
          regarding print quality or delivery must be raised within 7 days of receiving the
          order by contacting us at{" "}
          <a href="mailto:photentra@gmail.com">photentra@gmail.com</a>.
        </p>

        <h2>6. Content You Upload</h2>
        <p>
          You retain ownership of all photos, videos, and other content you upload to
          Photentra ("Your Content"). By uploading, you grant Photentra a limited,
          non-exclusive, royalty-free licence to store, process, and display Your Content
          solely as necessary to operate and improve the Service (for example, generating
          thumbnails or running face-recognition search within your gallery).
        </p>
        <p>
          You are solely responsible for ensuring you have the rights to upload and share
          any content, including consent from individuals who appear in photographs where
          required by law.
        </p>

        <h2>7. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Upload content that is illegal, defamatory, obscene, or infringes any third-party rights.</li>
          <li>Use the Service to harass, impersonate, or harm any person.</li>
          <li>Attempt to gain unauthorised access to any part of the Service or its infrastructure.</li>
          <li>Reverse engineer, scrape, or copy any part of the platform without written permission.</li>
          <li>Use the Service in any way that violates applicable Indian law or regulations.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that violate these rules,
          without notice or refund.
        </p>

        <h2>8. AI Features</h2>
        <p>
          Photentra uses AI models (including face recognition and a conversational
          assistant) to enhance your experience. AI outputs — including face matches,
          assistant responses, and automated suggestions — may not always be accurate. You
          are responsible for reviewing and verifying any AI-generated output before acting
          on it.
        </p>

        <h2>9. Studio Websites</h2>
        <p>
          Paid plan subscribers may be eligible for a hosted studio website under a
          Photentra subdomain. The content of your studio website is your responsibility.
          We reserve the right to remove content that violates these Terms or applicable law.
        </p>

        <h2>10. Intellectual Property</h2>
        <p>
          All software, design, trademarks, and content that are part of the Photentra
          platform (excluding Your Content) are owned by or licensed to Photentra. You may
          not copy, modify, distribute, or create derivative works from any part of the
          platform without our prior written consent.
        </p>

        <h2>11. Termination</h2>
        <p>
          You may close your account at any time by contacting us. We may suspend or
          terminate your account at our discretion if you breach these Terms or if we
          discontinue the Service. Upon termination, your right to use the Service ceases
          immediately. We will delete your data in accordance with our{" "}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>

        <h2>12. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind,
          express or implied, including warranties of merchantability, fitness for a
          particular purpose, or uninterrupted availability. We do not guarantee that the
          Service will be error-free or that data will never be lost.
        </p>

        <h2>13. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Photentra's total liability to you for any
          claim arising out of or relating to these Terms or the Service is limited to the
          amount you paid to Photentra in the 3 months preceding the claim. In no event will
          we be liable for indirect, incidental, consequential, or punitive damages.
        </p>

        <h2>14. Governing Law</h2>
        <p>
          These Terms are governed by the laws of India. Any disputes arising from these
          Terms or the Service shall be subject to the exclusive jurisdiction of the courts
          located in India.
        </p>

        <h2>15. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Material changes will be communicated
          by email or a notice within the app at least 7 days before they take effect.
          Continued use of the Service after the effective date constitutes your acceptance
          of the updated Terms.
        </p>

        <h2>16. Contact Us</h2>
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href="mailto:photentra@gmail.com">photentra@gmail.com</a>.
        </p>
      </main>
    </div>
  );
}

export default TermsOfService;
